import React, { useState, useRef, useEffect } from "react";
import DOLLUZ_CORP_thin_line_logo from "./assets/img/DOLLUZ_CORP_thin_line_logo.png";
import { apiFetch } from "./utils/api";
import { setVerifiedUser, getVerifiedUser } from "./utils/ticketSession";
import { FaTimes } from "react-icons/fa";
import MyTicketsList from "./MyTicketsList";
import { useNavigate, useLocation } from "react-router-dom";
import Select from "react-select";
import { useQuill } from "react-quilljs";
import "quill/dist/quill.snow.css";
import "./Tickets.css";

const Tickets = () => {
    const [emailError, setEmailError] = useState("");
    const [errors, setErrors] = useState({});
    const navigate = useNavigate();
    const location = useLocation();
    const [isSubmitting, setIsSubmitting] = useState(false); // ✅ Loading state
    const [showPriority, setShowPriority] = useState(false);
    const [showContact, setShowContact] = useState(false);
    const [showTeam, setShowTeam] = useState(false);
    const [showTesting, setShowTesting] = useState(false);
    const priorityRef = useRef(null);
    const contactRef = useRef(null);
    const teamRef = useRef(null);
    const testingRef = useRef(null);
    const [otpCode, setOtpCode] = useState("");
    const [otpVerified, setOtpVerified] = useState(false);
    const [otpError, setOtpError] = useState("");
    const [isSendingOtp, setIsSendingOtp] = useState(false);
    const [showOtpField, setShowOtpField] = useState(false);
    const [emailVerified, setEmailVerified] = useState(false);
    const [otpTimer, setOtpTimer] = useState(0);
    const otpTimerRef = useRef(null);
    const [issueType, setIssueType] = useState(""); // "Bug" or "Idea"
    const [activeTab, setActiveTab] = useState("create"); // "create" | "mytickets"

    // ✅ Custom div popup (replaces SweetAlert)
    const [popup, setPopup] = useState(null); // { icon, title, text }
    const showPopup = ({ icon, title, text }) => setPopup({ icon, title, text });
    const closePopup = () => setPopup(null);

    const startOtpTimer = () => {
        clearInterval(otpTimerRef.current);
        setOtpTimer(120);
        otpTimerRef.current = setInterval(() => {
            setOtpTimer((prev) => {
                if (prev <= 1) {
                    clearInterval(otpTimerRef.current);
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    };

    useEffect(() => {
        return () => clearInterval(otpTimerRef.current);
    }, []);

    useEffect(() => {
        // ✅ Skip OTP on "Raise Another Ticket" within the same session.
        // The in-memory store survives SPA navigation but is null after a
        // reload/tab-close, in which case we fall back to the normal check.
        const verified = getVerifiedUser();
        if (verified) {
            autoFillEmployee(verified);
        } else {
            checkAutoLogin();
        }

        // ✅ Pre-select the issue type when arriving via "Raise Another Bug/Idea".
        // Mirrors the radio onChange's data side-effects (issue_type + default
        // assignee); the [issueType] effect fills the Quill template.
        const presetType = location.state?.issueType;
        if (presetType === "Bug" || presetType === "Idea") {
            setIssueType(presetType);
            setFormData(prev => ({
                ...prev,
                issue_type: presetType,
                assignee: presetType === "Bug" ? "Pavithran" : "Anandth Shoban Balasubramanian",
            }));
        }

        // ✅ Open straight on the My Tickets tab when arriving from "Track my tickets"
        if (location.state?.openTab === "mytickets") {
            setActiveTab("mytickets");
        }
    }, []);

    const checkAutoLogin = async () => {
        try {
            const res = await apiFetch("/api/tickets/check-auth", {
                method: "GET",
                credentials: "include"
            });

            const data = await res.json();

            if (data.loggedIn) {
                autoFillEmployee(data.emp);
            }
        } catch (err) {
            console.log("Auth check error", err);
        }
    };

    const autoFillEmployee = (emp) => {
        setFormData({
            ...formData,
            emp_id: emp.emp_id,
            email: emp.emp_mail_id,
        });

        setEmailVerified(true);
        setOtpVerified(true);
        setShowOtpField(false);
    };

    const teamOptions = ["Dev Team", "QA Team", "Ops Team"];
    const testingTypeOptions = ["Manual", "Automation"];

    const deviceOptions = [
        { value: "Desktop", label: "Desktop" },
        { value: "Tablet", label: "Tablet" },
        { value: "Mobile - Android", label: "Mobile - Android" },
        { value: "Mobile - iOS", label: "Mobile - iOS" },
    ];

    // ⚠️ Exactly ONE useQuill instance per component. react-quilljs merges the
    // caller's options into a module-level `modules` object and hands that same
    // object to every editor it creates; Quill then rewrites `toolbar` in it to
    // the container it built for the first editor, so a second instance silently
    // adopts the first one's toolbar and renders without its own. Bug and Idea
    // share this single editor — only one is ever visible at a time.
    const { quill, quillRef } = useQuill({
        theme: "snow",
        modules: {
            toolbar: [["bold", "italic", "underline", "strike"],
            [{ list: "ordered" }, { list: "bullet" }],
            [{ align: [] }], ["clean"],],
        },
        formats: ["bold", "italic", "underline", "strike", "list", "bullet", "align",],
    });

    // listen for changes
    useEffect(() => {
        const editor = (issueType === "Bug" || issueType === "Idea") ? quill : null;
        if (!editor) return;

        if (editor.getText().trim().length === 0) {
            // 🧹 Remove any existing empty <p> elements
            const paras = editor.root.querySelectorAll("p");
            paras.forEach((p) => {
                if (!p.textContent.trim()) p.remove();
            });

            // 📝 Insert default structured template
            editor.root.innerHTML = `
<p><strong>Steps to Reproduce:</strong><br><br></p>
<p><strong>Test Data:</strong><br><br></p>
<p><strong>Expected Result:</strong><br><br></p>
<p><strong>Actual Result:</strong></p>
        `;
        }

        const handler = () => handleDescriptionChange(editor.root.innerHTML);
        editor.on("text-change", handler);

        return () => {
            editor.off("text-change", handler);
        };
    }, [issueType, quill]);

    const priorities = [
        {
            value: "High",
            label: "High",
            icon: (
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <i className="fa-solid fa-angle-up" style={{ color: "red", fontSize: "15px", lineHeight: "0.5" }}></i>
                    <i className="fa-solid fa-angle-up" style={{ color: "red", fontSize: "11px", lineHeight: "0.5" }}></i>
                    <i className="fa-solid fa-angle-up" style={{ color: "red", fontSize: "9px", lineHeight: "0.5" }}></i>
                </span>
            ),
        },
        {
            value: "Medium",
            label: "Medium",
            icon: (
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <i className="fa-solid fa-angle-up" style={{ color: "#FF8C00", fontSize: "14px", lineHeight: "0.6" }}></i>
                    <i className="fa-solid fa-angle-up" style={{ color: "#FF8C00", fontSize: "10px", lineHeight: "0.6" }}></i>
                </span>
            ),
        },
        {
            value: "Low",
            label: "Low",
            icon: (
                <span style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                    <i className="fa-solid fa-angle-up" style={{ color: "green", fontSize: "14px", lineHeight: "0.6" }}></i>
                </span>
            ),
        },
    ];

    useEffect(() => {
        const handleOutsideClick = (e) => {
            // Close Priority dropdown if clicked outside
            if (priorityRef.current && !priorityRef.current.contains(e.target)) {
                setShowPriority(false);
            }

            // Close Contact dropdown if clicked outside
            if (contactRef.current && !contactRef.current.contains(e.target)) {
                setShowContact(false);
            }

            // Close Reporting Team dropdown if clicked outside
            if (teamRef.current && !teamRef.current.contains(e.target)) {
                setShowTeam(false);
            }

            // Close Testing Type dropdown if clicked outside
            if (testingRef.current && !testingRef.current.contains(e.target)) {
                setShowTesting(false);
            }
        };

        document.addEventListener("mousedown", handleOutsideClick);

        return () => {
            document.removeEventListener("mousedown", handleOutsideClick);
        };
    }, []);

    const [formData, setFormData] = useState({
        emp_id: "",
        email: "",
        issue_type: "",
        summary: "",
        description: "",
        attachment_file: null,
        assignee: "",
        priority_level: "",
        reporting_team: "",
        testing_type: "",
        device_tested: [],
    });

    const fileInputRef = useRef(null);

    const handleFileChange = (e) => {
        const files = Array.from(e.target.files);
        if (!files.length) return;
        // ✅ LIMIT: max 3 total
        if ((formData.attachments?.length || 0) + files.length > 3) {
            showPopup({
                icon: "error",
                title: "Limit Exceeded",
                text: "You can upload a maximum of 3 files only",
            });
            e.target.value = null;
            return;
        }

        // ✅ LIMIT: 3 MB across ALL attachments combined (not per file)
        const MAX_TOTAL_BYTES = 3 * 1024 * 1024;
        const existingSize = (formData.attachments || []).reduce((sum, f) => sum + f.size, 0);
        const incomingSize = files.reduce((sum, f) => sum + f.size, 0);
        if (existingSize + incomingSize > MAX_TOTAL_BYTES) {
            showPopup({
                icon: "error",
                title: "Size Limit Exceeded",
                text: "Total attachment size must not exceed 3 MB.",
            });
            e.target.value = null;
            return;
        }

        const validFiles = [];

        const secretPatterns = [
            /SG\.[A-Za-z0-9_-]+/g, // SendGrid API Key pattern
            /ghp_[A-Za-z0-9]+/g,   // GitHub Personal Access Token
            /AIza[A-Za-z0-9-_]{35}/g // Example: Google API Key
        ];

        const checkFileForSecrets = (file) => {
            return new Promise((resolve, reject) => {
                const reader = new FileReader();
                reader.onload = (event) => {
                    const content = event.target.result;
                    for (let pattern of secretPatterns) {
                        if (pattern.test(content)) {
                            resolve(file.name); // found a secret
                            return;
                        }
                    }
                    resolve(null); // no secret
                };
                reader.onerror = () => reject(file.name);
                reader.readAsText(file); // read file as text
            });
        };

        const processFiles = async () => {
            const existingNames = (formData.attachments || []).map(f => f.name);

            for (let file of files) {
                if (existingNames.includes(file.name)) {
                    showPopup({
                        icon: "warning",
                        title: "Duplicate File",
                        text: `"${file.name}" is already attached.`,
                    });
                    continue;
                }

                try {
                    const secretFile = await checkFileForSecrets(file);
                    if (secretFile) {
                        showPopup({
                            icon: "error",
                            title: "Invalid File",
                            text: `${file.name} contains a sensitive key and cannot be uploaded.`,
                        });
                        continue;
                    }
                    validFiles.push(file);
                } catch {
                    showPopup({
                        icon: "error",
                        title: "Error",
                        text: `Cannot read file ${file.name}`,
                    });
                }
            }

            if (validFiles.length) {
                setFormData((prev) => ({
                    ...prev,
                    attachments: [...(prev.attachments || []), ...validFiles],
                }));
            }

            e.target.value = null; // allow re-uploading same file name
        };

        processFiles();
    };

    const removeFile = (index) => {
        setFormData((prev) => {
            const updated = [...prev.attachments];
            updated.splice(index, 1);
            return { ...prev, attachments: updated };
        });

        if (fileInputRef.current) fileInputRef.current.value = null;
    };

    // ✅ Handle field changes
    const handleChange = (e) => {
        const { name, value } = e.target;

        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));

        if (name === "email") {
            setEmailError("");
        }
    };

    const handleEmailSubmit = async () => {
        const value = formData.email;

        if (!value || !value.includes("@")) {
            setEmailError("Invalid email format.");
            return;
        }

        try {
            const res = await apiFetch(`/api/tickets/employee/${value}`);
            // ❗ If backend returns 401 or 404, handle it gracefully
            if (!res.ok) {
                const errorData = await res.json().catch(() => null);

                if (res.status === 401 && errorData?.message) {
                    // 🔥 Show access denied error properly
                    setEmailError(errorData.message);
                    return;
                }

                throw new Error("Employee not found");
            }

            const data = await res.json();

            setFormData((prev) => ({
                ...prev,
                emp_id: data.emp_id,
            }));

            // ✅ Email is now verified
            setEmailVerified(true);
            // ✅ Start Loader
            setIsSendingOtp(true);

            const otpResponse = await apiFetch("/api/tickets/send-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: value }),
            });

            // ✅ Stop Loader
            setIsSendingOtp(false);

            if (otpResponse.ok) {
                setShowOtpField(true);
                startOtpTimer();
                showPopup({
                    icon: "success",
                    title: "OTP Sent!",
                    text: "Please check your registered email for the OTP. It expires in 2 minutes.",
                });
            } else {
                showPopup({
                    icon: "error",
                    title: "Failed to send OTP",
                    text: "Please try again later.",
                });
            }

            setOtpVerified(false); // reset OTP verification
            setOtpCode("");
            setOtpError("");

        } catch (err) {
            setIsSendingOtp(false);
            setShowOtpField(false);
            setEmailError("This email is not registered with Dolluz Corp. Please contact admin (admin@dolluzcorp.com).");
            setFormData((prev) => ({ ...prev, emp_id: null, emp_first_name: "", emp_last_name: "" }));
        }
    };

    const handleResendOtp = async () => {
        setIsSendingOtp(true);
        setOtpCode("");
        setOtpError("");

        try {
            const otpResponse = await apiFetch("/api/tickets/send-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: formData.email }),
            });

            setIsSendingOtp(false);

            if (otpResponse.ok) {
                startOtpTimer();
                showPopup({
                    icon: "success",
                    title: "OTP Resent!",
                    text: "A new OTP has been sent to your email. It expires in 2 minutes.",
                });
            } else {
                showPopup({
                    icon: "error",
                    title: "Failed to resend OTP",
                    text: "Please try again later.",
                });
            }
        } catch (err) {
            setIsSendingOtp(false);
            showPopup({
                icon: "error",
                title: "Error",
                text: "Failed to resend OTP. Please try again.",
            });
        }
    };

    const handleOtpChange = (e) => {
        const value = e.target.value.replace(/\D/g, ""); // Only digits
        setOtpCode(value);

        if (value.length === 6) {
            verifyOTP(value);
        } else {
            setOtpError(""); // Clear error while typing
        }
    };

    const verifyOTP = async (code) => {
        setOtpError("");

        try {
            const res = await apiFetch("/api/tickets/verify-otp", {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ email: formData.email, otp: code }),
            });

            const data = await res.json();

            if (res.ok) {
                setOtpVerified(true);
                setOtpError("");

                // ✅ Record the verified user as soon as OTP passes (not only on
                // submit) so navigating to "Track my tickets" skips OTP.
                setVerifiedUser({
                    emp_id: formData.emp_id,
                    emp_mail_id: formData.email,
                });
            } else {
                setOtpError(data.message || "Invalid OTP");
                setOtpVerified(false);
            }
        } catch (err) {
            setOtpError("Error verifying OTP. Please try again.");
            setOtpVerified(false);
        }
    };

    const handleDescriptionChange = (value) => {
        setFormData((prev) => ({
            ...prev,
            description: value,
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setIsSubmitting(true);

        // Define required fields dynamically
        let requiredFields = ["emp_id", "issue_type", "summary", "reporting_team"];

        if (issueType === "Bug") {
            // Bug-specific required fields
            requiredFields.push("priority_level", "testing_type", "devicesTested");
        }
        // Idea-specific fields: summary and description are already included

        // Validate required fields
        for (let field of requiredFields) {
            if (!formData[field] || (Array.isArray(formData[field]) && !formData[field].length)) {
                showPopup({
                    icon: "error",
                    title: "Validation Error",
                    text: `${field.replace(/_/g, " ")} is required`,
                });
                setIsSubmitting(false);
                return;
            }
        }

        // Validate Quill description length
        let plainTextDescription = "";
        if ((issueType === "Bug" || issueType === "Idea") && quill) {
            plainTextDescription = quill.getText().trim();
        }

        if (plainTextDescription.length < 300 || plainTextDescription.length > 5000) {
            showPopup({
                icon: "error",
                title: "Validation Error",
                text: "Description must be between 300 and 5000 characters",
            });
            setIsSubmitting(false);
            return;
        }

        // Build FormData for submission
        const formDataToSend = new FormData();

        // ✅ Append emp_id FIRST so multer can use it in the saved filename
        if (formData.emp_id) {
            formDataToSend.append("emp_id", formData.emp_id);
        }

        // Append all fields from formData
        for (const key in formData) {
            if (key === "emp_id") continue; // already appended above
            if (key !== "attachments" && key !== "description" && formData[key]) { // skip description
                if (Array.isArray(formData[key])) {
                    formDataToSend.append(key, JSON.stringify(formData[key]));
                } else {
                    formDataToSend.append(key, formData[key]);
                }
            }
        }

        // Append description from Quill (single shared editor)
        const editor = (issueType === "Bug" || issueType === "Idea") ? quill : null;

        if (editor) {
            formDataToSend.append("description", editor.root.innerHTML);
        } else {
            formDataToSend.append("description", "");
        }

        // ✅ Append multiple attachments (important part)
        if (formData.attachments && formData.attachments.length > 0) {
            formData.attachments.forEach((file) => {
                formDataToSend.append("attachments", file);
            });
        }

        // Append created_by as emp_id
        if (formData.emp_id) {
            formDataToSend.append("created_by", formData.emp_id);
        } else {
            showPopup({
                icon: "warning",
                title: "Validation Error",
                text: "Please select a valid employee before submitting the ticket.",
            });
            setIsSubmitting(false);
            return;
        }

        try {
            const res = await apiFetch("/api/tickets/submit", {
                method: "POST",
                body: formDataToSend,
            });

            const data = await res.json();
            if (!res.ok) throw new Error(data.message || data.error || "Error submitting ticket");

            // ✅ Persist the verified user in memory so "Raise Another Ticket"
            // skips OTP within this session (cleared on reload/tab-close).
            setVerifiedUser({
                emp_id: formData.emp_id,
                emp_mail_id: formData.email,
            });

            // Redirect to Thank_You page
            navigate("/thank-you", {
                state: {
                    employeeName: data.employeeName,
                    ticketId: data.ticket_id,
                    issueType: data.issueType,
                },
            });

            setTimeout(() => {
                setFormData({
                    emp_id: "",
                    issue_type: "",
                    summary: "",
                    description: "",
                    attachment_file: [],
                    assignee: "",
                    priority_level: "",
                    reporting_team: "",
                    testing_type: "",
                    device_tested: [],
                });

                setErrors({});
                if (fileInputRef.current) fileInputRef.current.value = null;
            }, 0);

        } catch (err) {
            showPopup({
                icon: "error",
                title: "Submission Error",
                text: err.message,
            });
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="tickets-container">
            <div className={`ticket-card ${activeTab === "mytickets" ? "wide" : ""}`}>
                <div className="ticket-logo">
                    <a className="navbar-brand" href="#">
                        <div className="ticket-logo">
                            <a className="navbar-brand" href="#">
                                <img
                                    src={DOLLUZ_CORP_thin_line_logo}
                                    alt="dbug logo_eagle"
                                />
                            </a>
                        </div>
                    </a>
                </div>

                <form className="ticket-form" onSubmit={handleSubmit}>
                    {/* Email */}
                    <label>Email {errors.email && <span style={{ color: "red" }}>* </span>} </label>
                    <div className="email-input-container">
                        <input
                            type="email"
                            name="email"
                            value={formData.email}
                            onChange={handleChange}
                            disabled={emailVerified}  // ✅ Disable when verified
                            className={emailError ? "invalid" : ""}
                        />

                        {/* Show Arrow Icon Only When "@" Exists AND not yet verified */}
                        {(formData.email || "").includes("@") && !emailVerified && !showOtpField && (
                            <i className="fa-solid fa-arrow-right email-action-icon" onClick={handleEmailSubmit}></i>
                        )}

                        {/* ✅ Show Success Icon When Email is Verified (not waiting for OTP) */}
                        {emailVerified && (
                            <i className="fa-solid fa-circle-check email-valid-icon"></i>
                        )}
                    </div>

                    {emailError && <p style={{ color: "red" }}>{emailError}</p>}

                    {/* Loader while sending OTP */}
                    {isSendingOtp && <div className="spinner"></div>}

                    {/* OTP Section */}
                    {showOtpField && !otpVerified && (
                        <div className="otp-section mt-2">
                            <label>Enter OTP</label>
                            <div className="otp-input-container">
                                <input
                                    type="text"
                                    value={otpCode}
                                    onChange={handleOtpChange}
                                    maxLength={6}
                                    className={otpError ? "invalid" : otpCode.length === 6 && !otpError ? "valid" : ""}
                                />
                                {otpCode.length === 6 && !otpError && otpVerified && (
                                    <i className="fa-solid fa-circle-check email-valid-icon"></i>
                                )}
                            </div>
                            {otpError && <p style={{ color: "red" }}>{otpError}</p>}
                            <div className="otp-timer-row">
                                {otpTimer > 0 ? (
                                    <p className="otp-timer-text">
                                        OTP expires in <span className="otp-timer-count">{Math.floor(otpTimer / 60)}:{String(otpTimer % 60).padStart(2, "0")}</span>
                                    </p>
                                ) : (
                                    <p className="otp-expired-text">
                                        OTP expired.{" "}
                                        <span className="otp-resend-link" onClick={handleResendOtp}>Resend OTP</span>
                                    </p>
                                )}
                            </div>
                        </div>
                    )}

                    {/* Tabs — only once the email is verified */}
                    {otpVerified && (
                        <div className="ticket-tabs">
                            <button
                                type="button"
                                className={`ticket-tab ${activeTab === "create" ? "active" : ""}`}
                                onClick={() => setActiveTab("create")}
                            >
                                Create Ticket
                            </button>
                            <button
                                type="button"
                                className={`ticket-tab ${activeTab === "mytickets" ? "active" : ""}`}
                                onClick={() => setActiveTab("mytickets")}
                            >
                                Track My Tickets
                            </button>
                        </div>
                    )}

                    <div
                        className={`ticket-fields tab-panel ${(!formData.emp_id || !otpVerified) ? "blurred" : ""}`}
                        style={{ display: activeTab === "create" ? "block" : "none" }}
                    >

                        {otpVerified && (
                            <div className="issue-section">
                                <h3>Create Issue:</h3>

                                <div className="issue-options">
                                        <label>
                                            <input
                                                type="radio"
                                                value="Bug"
                                                checked={issueType === "Bug"}
                                                onChange={(e) => {
                                                    const newType = e.target.value;
                                                    setIssueType(newType);

                                                    // Reset all dependent fields
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        issue_type: newType,
                                                        assignee: "Pavithran",
                                                        summary: "",
                                                        description: "", // Quill will handle default template
                                                        attachments: [],
                                                        priority: "",
                                                        team: "",
                                                        testing_type: "",
                                                        devicesTested: [],
                                                    }));

                                                    // Clear file input
                                                    if (fileInputRef.current) fileInputRef.current.value = null;

                                                    // Set default template in Quill
                                                    if (quill) {
                                                        quill.root.innerHTML = `
                                                        <p><strong>Steps to Reproduce:</strong><br></p>
                                                            <p><strong>Test Data:</strong><br></p>
                                                            <p><strong>Expected Result:</strong><br></p>
                                                            <p><strong>Actual Result:</strong><br></p>
                                                        `;
                                                    }
                                                }}
                                            />
                                            Bug
                                        </label>

                                        <label>
                                            <input
                                                type="radio"
                                                value="Idea"
                                                checked={issueType === "Idea"}
                                                onChange={(e) => {
                                                    const newType = e.target.value;
                                                    setIssueType(newType);

                                                    // Reset all dependent fields
                                                    setFormData(prev => ({
                                                        ...prev,
                                                        issue_type: newType,
                                                        assignee: "Anandth Shoban Balasubramanian",
                                                        summary: "",
                                                        description: "",
                                                        attachments: [],
                                                        priority: "",
                                                        team: "",
                                                        testing_type: "",
                                                        devicesTested: [],
                                                    }));

                                                    // Clear file input
                                                    if (fileInputRef.current) fileInputRef.current.value = null;

                                                    // Set default template in Quill
                                                    if (quill) {
                                                        quill.root.innerHTML = `
                                                        <p><strong>Steps to Reproduce:</strong><br></p>
                                                            <p><strong>Test Data:</strong><br></p>
                                                            <p><strong>Expected Result:</strong><br></p>
                                                            <p><strong>Actual Result:</strong><br></p>
                                                        `;
                                                    }
                                                }}
                                            />
                                            Idea
                                        </label>
                                    </div>
                            </div>
                        )}

                        {/* Summary / Description / Attachments — one shared block for both
                            issue types (only one type is ever active at a time) */}
                        <div style={{ display: (issueType === "Bug" || issueType === "Idea") ? "block" : "none" }}>
                            <label>Summary <span style={{ color: "red" }}>*</span></label>
                            <input type="text" name="summary" value={formData.summary} onChange={handleChange} />

                            <label>Description <span style={{ color: "red" }}>*</span></label>
                            <div ref={quillRef} style={{ height: "150px", marginBottom: "20px" }} />

                            {/* Attachment */}
                            <div className="attachment-field" style={{ position: "relative", display: "inline-block", width: "100%" }}>
                                <label>Attachments</label>
                                <input
                                    type="file"
                                    name="attachments"
                                    ref={fileInputRef}
                                    multiple  // ✅ allow selecting multiple files
                                    onChange={handleFileChange}
                                    disabled={!formData.emp_id}
                                    style={{ width: "100%" }}
                                />

                                {/* Show file names + X icon */}
                                {formData.attachments && formData.attachments.length > 0 && (
                                    <div style={{ marginTop: "8px" }}>
                                        {formData.attachments.map((file, index) => (
                                            <div key={index} style={{ display: "flex", alignItems: "center", marginBottom: "5px" }}>
                                                <span>{file.name}</span>
                                                <FaTimes
                                                    style={{
                                                        marginLeft: "10px",
                                                        cursor: "pointer",
                                                        color: "gray",
                                                    }}
                                                    onClick={() => removeFile(index)}
                                                    title="Remove file"
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>

                        {issueType === "Bug" && (
                            <div className="ticket-fields">
                                <label>Assignee <span style={{ color: "red" }}>*</span></label>
                                <select disabled>
                                    <option selected>{issueType === "Bug" ? "Pavithran" : "Anandth Shoban Balasubramanian"}</option>
                                </select>

                                <label>
                                    Priority <span style={{ color: "red" }}>*</span>
                                </label>

                                <div className="priority-custom-dropdown" ref={priorityRef}>
                                    <div
                                        className="selected-option"
                                        onClick={() => setShowPriority((prev) => !prev)}
                                    >
                                        <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
                                            {formData.priority_level
                                                ? priorities.find((p) => p.value === formData.priority_level)?.icon
                                                : null}
                                            {formData.priority_level
                                                ? priorities.find((p) => p.value === formData.priority_level)?.label
                                                : "Select Priority"}
                                        </div>
                                        <i className="fa-solid fa-angle-down priority-arrow"></i>
                                    </div>

                                    {showPriority && (
                                        <div className="priority-dropdown-menu">
                                            {priorities.map((p) => (
                                                <div
                                                    key={p.value}
                                                    className="priority-dropdown-item"
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleChange({
                                                            target: { name: "priority_level", value: p.value }, // ✅ fixed here
                                                        });
                                                        setShowPriority(false);
                                                    }}
                                                >
                                                    {p.icon} {p.label}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <label>
                                    Reporting Team <span style={{ color: "red" }}>*</span>
                                </label>
                                <div
                                    className={`field-dropdown ${showTeam ? "open" : ""}`}
                                    ref={teamRef}
                                >
                                    <div
                                        className="field-selected"
                                        onClick={() => setShowTeam((prev) => !prev)}
                                    >
                                        {formData.reporting_team || "Select Team"}
                                        <i className="fa-solid fa-angle-down"></i>
                                    </div>

                                    {showTeam && (
                                        <div className="field-menu">
                                            {teamOptions.map((opt) => (
                                                <div
                                                    key={opt}
                                                    className={`field-item ${formData.reporting_team === opt ? "active" : ""}`}
                                                    onClick={() => {
                                                        handleChange({ target: { name: "reporting_team", value: opt } });
                                                        setShowTeam(false);
                                                    }}
                                                >
                                                    {opt}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <label>Testing Type <span style={{ color: "red" }}>*</span></label>
                                <div
                                    className={`field-dropdown ${showTesting ? "open" : ""}`}
                                    ref={testingRef}
                                >
                                    <div
                                        className="field-selected"
                                        onClick={() => setShowTesting((prev) => !prev)}
                                    >
                                        {formData.testing_type || "Select Testing Type"}
                                        <i className="fa-solid fa-angle-down"></i>
                                    </div>

                                    {showTesting && (
                                        <div className="field-menu">
                                            {testingTypeOptions.map((opt) => (
                                                <div
                                                    key={opt}
                                                    className={`field-item ${formData.testing_type === opt ? "active" : ""}`}
                                                    onClick={() => {
                                                        handleChange({ target: { name: "testing_type", value: opt } });
                                                        setShowTesting(false);
                                                    }}
                                                >
                                                    {opt}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <label>Device Tested <span style={{ color: "red" }}>*</span></label>
                                <Select
                                    isMulti
                                    name="devicesTested"
                                    options={deviceOptions}
                                    className="basic-multi-select"
                                    classNamePrefix="select"
                                    value={deviceOptions.filter(opt => formData.devicesTested?.includes(opt.value))}
                                    onChange={(selected) =>
                                        setFormData(prev => ({ ...prev, devicesTested: selected.map(opt => opt.value) }))
                                    }
                                />

                                <button type="submit" disabled={isSubmitting} className="submit-btn">
                                    {isSubmitting ? "Submitting..." : "Submit"}
                                </button>

                                <p className="ticket-note">
                                    <span className="note-title">Note:</span>

                                    <div className="note-row">
                                        <span className="note-index">1.</span>
                                        <span className="note-content">
                                            Once submitted, your defect will be routed to the respective development team.
                                            We appreciate your patience as per SLA window.
                                        </span>
                                    </div>

                                    <div className="note-row">
                                        <span className="note-index">2.</span>
                                        <span className="note-content">
                                            SLA: Low = &lt;48 hrs, Medium = &lt;24 hrs, High = &lt;8 hrs (Business Hours)
                                            <span className="note-icon-text">
                                                <i className="fa-solid fa-circle-exclamation"></i> Inappropriate priority will be deprioritized to max ETA.
                                            </span>
                                        </span>
                                    </div>
                                </p>
                            </div>
                        )}


                        {issueType === "Idea" && (
                            <div className="ticket-fields">
                                <label>Assignee <span style={{ color: "red" }}>*</span></label>
                                <select disabled>
                                    <option selected>{issueType === "Bug" ? "Pavithran" : "Anandth Shoban Balasubramanian"}</option>
                                </select>

                                <label>
                                    Reporting Team <span style={{ color: "red" }}>*</span>
                                </label>
                                <div
                                    className={`field-dropdown ${showTeam ? "open" : ""}`}
                                    ref={teamRef}
                                >
                                    <div
                                        className="field-selected"
                                        onClick={() => setShowTeam((prev) => !prev)}
                                    >
                                        {formData.reporting_team || "Select Team"}
                                        <i className="fa-solid fa-angle-down"></i>
                                    </div>

                                    {showTeam && (
                                        <div className="field-menu">
                                            {teamOptions.map((opt) => (
                                                <div
                                                    key={opt}
                                                    className={`field-item ${formData.reporting_team === opt ? "active" : ""}`}
                                                    onClick={() => {
                                                        handleChange({ target: { name: "reporting_team", value: opt } });
                                                        setShowTeam(false);
                                                    }}
                                                >
                                                    {opt}
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>

                                <button type="submit" disabled={isSubmitting} className="submit-btn">
                                    {isSubmitting ? "Submitting..." : "Submit"}
                                </button>

                                <p className="ticket-note">
                                    <span className="note-title">Note:</span>

                                    <div className="note-row">
                                        <span className="note-content">
                                            Once submitted, your idea will be routed to the respective development team.
                                            We appreciate your patience during our review.
                                        </span>
                                    </div>
                                </p>
                            </div>
                        )}

                    </div>
                </form>

                {/* My Tickets tab — outside the <form> so its inputs can't submit it.
                    Mounted once the email is verified and then only hidden, never
                    unmounted: remounting on every tab switch refetched the list and
                    flashed the spinner, which read as a page reload. */}
                {otpVerified && (
                    <div
                        className="tab-panel"
                        style={{ display: activeTab === "mytickets" ? "block" : "none" }}
                    >
                        <MyTicketsList
                            empId={formData.emp_id}
                            showPopup={showPopup}
                            onCreateTicket={() => setActiveTab("create")}
                        />
                    </div>
                )}
            </div>

            {/* ✅ Custom div popup (replaces SweetAlert) */}
            {popup && (
                <div className="dpopup-overlay" onClick={closePopup}>
                    <div className="dpopup-card" onClick={(e) => e.stopPropagation()}>
                        <button className="dpopup-close" onClick={closePopup} aria-label="Close">
                            <FaTimes />
                        </button>
                        <div className={`dpopup-icon dpopup-icon-${popup.icon}`}>
                            {popup.icon === "success" && <i className="fa-solid fa-circle-check"></i>}
                            {popup.icon === "error" && <i className="fa-solid fa-circle-xmark"></i>}
                            {popup.icon === "warning" && <i className="fa-solid fa-triangle-exclamation"></i>}
                            {!["success", "error", "warning"].includes(popup.icon) && <i className="fa-solid fa-circle-info"></i>}
                        </div>
                        {popup.title && <div className="dpopup-title">{popup.title}</div>}
                        {popup.text && <div className="dpopup-text">{popup.text}</div>}
                        <button className="dpopup-ok" onClick={closePopup}>OK</button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Tickets;
